
import {of as observableOf,  Observable ,  AsyncSubject } from 'rxjs';

import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  Classification,
  ClassificationValueDto,
  ClassificationTypeDto,
  LocalizedValue,
  LocalizedValueLanguage } from 'shared/service-clients/classification';
import { TranslationHelperService } from 'core/translation-helper.service';

@Injectable()
export class ClassificationService {
  private cacheByValueId: Map<string, string>;
  private cacheByTypeId: Map<string, string[]>;
  private cacheByTypeCode: Map<string, ClassificationValueDto[]>;

  public cacheReadySubject = new AsyncSubject();

  constructor(
    private classificationService: Classification,
    private translationHelperService: TranslationHelperService
  ) {
    this.cacheByValueId = new Map<string, string>();
    this.cacheByTypeId = new Map<string, string[]>();
    this.cacheByTypeCode = new Map<string, ClassificationValueDto[]>();
  }

  load() {
    this.loadStatic();

    this.classificationService.getClassificationValues().subscribe(w => {
      const list = w.result;
      list.forEach(x => {
        this.cacheByValueId.set(x.classificationValueId, x.classificationValueName);

        if (this.cacheByTypeId.get(x.classificationType.classificationTypeId)) {
          this.cacheByTypeId.get(x.classificationType.classificationTypeId).push(x.classificationValueName);
        } else {
          this.cacheByTypeId.set(x.classificationType.classificationTypeId, []);
          this.cacheByTypeId.get(x.classificationType.classificationTypeId).push(x.classificationValueName);
        }

        if (this.cacheByTypeCode.get(x.classificationType.classificationTypeCode)) {
          this.cacheByTypeCode.get(x.classificationType.classificationTypeCode).push(x);
        } else {
          this.cacheByTypeCode.set(x.classificationType.classificationTypeCode, []);
          this.cacheByTypeCode.get(x.classificationType.classificationTypeCode).push(x);
        }
      });
      console.log('Classification Cache Ready!');
      this.cacheReadySubject.next(true);
      this.cacheReadySubject.complete();
    });
  }

  private loadStatic() {
    const priorityClassificationType = new ClassificationTypeDto({
      classificationTypeCode: 'PRIORITY',
      classificationTypeId: '',
      classificationTypeName: 'Priority',
      names: null
    });

    const yesOption = new ClassificationValueDto({
      classificationType: priorityClassificationType,
      classificationValueCode: 'YES',
      classificationValueId: 'true',
      classificationValueName: this.translationHelperService.translate('Yes'),
      names: [new LocalizedValue({
        language: LocalizedValueLanguage.En,
        value: this.translationHelperService.translate('Yes')
      })]
    });

    const noOption = new ClassificationValueDto({
      classificationType: priorityClassificationType,
      classificationValueCode: 'NO',
      classificationValueId: 'false',
      classificationValueName: this.translationHelperService.translate('No'),
      names: [new LocalizedValue({
        language: LocalizedValueLanguage.En,
        value: this.translationHelperService.translate('No')
      })]
    });

    this.cacheByValueId.set(yesOption.classificationValueId, yesOption.classificationValueName);
    this.cacheByValueId.set(noOption.classificationValueId, noOption.classificationValueName);
    this.cacheByTypeCode.set(priorityClassificationType.classificationTypeCode, []);
    this.cacheByTypeCode.get(priorityClassificationType.classificationTypeCode).push(yesOption);
    this.cacheByTypeCode.get(priorityClassificationType.classificationTypeCode).push(noOption);


    const activeClassificationType = new ClassificationTypeDto({
      classificationTypeCode: 'ISACTIVE',
      classificationTypeId: '',
      classificationTypeName: 'IsActive',
      names: null
    });

    const activeYesOption = new ClassificationValueDto({
      classificationType: activeClassificationType,
      classificationValueCode: 'YES',
      classificationValueId: 'true',
      classificationValueName: this.translationHelperService.translate('Active'),
      names: [new LocalizedValue({
        language: LocalizedValueLanguage.En,
        value: this.translationHelperService.translate('Active')
      })]
    });

    const activeNoOption = new ClassificationValueDto({
      classificationType: activeClassificationType,
      classificationValueCode: 'NO',
      classificationValueId: 'false',
      classificationValueName: this.translationHelperService.translate('Inactive'),
      names: [new LocalizedValue({
        language: LocalizedValueLanguage.En,
        value: this.translationHelperService.translate('Inactive')
      })]
    });

    this.cacheByValueId.set(activeYesOption.classificationValueId, activeYesOption.classificationValueName);
    this.cacheByValueId.set(activeNoOption.classificationValueId, activeNoOption.classificationValueName);
    this.cacheByTypeCode.set(activeClassificationType.classificationTypeCode, []);
    this.cacheByTypeCode.get(activeClassificationType.classificationTypeCode).push(activeYesOption);
    this.cacheByTypeCode.get(activeClassificationType.classificationTypeCode).push(activeNoOption);
  }

  addCacheByValueId(valueId: string, value: string): void {
    this.cacheByValueId.set(valueId, value);
  }

  getClassificationValueByValueId(valueId: string): Observable<ClassificationValueDto> {
    return this.classificationService.getClassificationValueById(valueId).pipe(map(x => x.result));
  }

  getClassificationValueNameByValueId(valueId: string): Observable<string> {
    if (valueId == null) {
      return null;
    }
    const items = this.cacheByValueId.get(valueId.toString());
    if (items) {
      return observableOf(items);
    } else {
        return this.classificationService.getClassificationValueById(valueId).pipe(map(x => x.result?.classificationValueName));
    }
  }

  getClassificationValueNamesByTypeId(typeId: string): Observable<string[]> {
    const items = this.cacheByTypeId.get(typeId.toString());
    if (items) {
      return observableOf(items);
    } else {
      return this.classificationService.getClassificationValuesByType(typeId).pipe(map(x => x.result.map(y => y.classificationValueName)));
    }
  }

  getClassificationValuesByTypeCode(typeCode: string): Observable<ClassificationValueDto[]> {
    const items = this.cacheByTypeCode.get(typeCode);
    if (items) {
      return observableOf(items);
    } else {
      return this.classificationService.getClassificationValuesByTypeCode(typeCode).pipe(map(x => x.result));
    }
  }

  getClassificationValuesByType(typeId: string): Observable<ClassificationValueDto[]> {
      return this.classificationService.getClassificationValuesByType(typeId).pipe(map(x => x.result));
  }
}

export class Item {
  value: any;
  label: string;
  names: LocalizedValue[];

  constructor(value, label: string, name = '') {
    this.value = value;
    this.label = label;
    this.names = [new LocalizedValue({
      language: LocalizedValueLanguage.En,
      value: name ? name : label
    })];
  }
}
